import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import PlgSignupLayout from "plg/screens/Signup/PlgSignupLayout";
import GoogleSsoButton from "plg/screens/Signup/GoogleSsoButton";
import MicrosoftSsoButton from "plg/screens/Signup/MicrosoftSsoButton";
import MagicLinkForm from "plg/screens/Signup/MagicLinkForm";
import MagicLinkLanding from "plg/screens/Signup/MagicLinkLanding";
import HumanGate from "plg/screens/Signup/HumanGate";
import SignInErrorNote from "plg/screens/Signup/SignInErrorNote";
import BusinessEmailCard from "plg/screens/Signup/BusinessEmailCard";
import MagicLinkSentCard from "plg/screens/Signup/MagicLinkSentCard";
import CountryBlockedCard from "plg/screens/Signup/CountryBlockedCard";
import usePlgSignupConfig from "plg/screens/Signup/usePlgSignupConfig";

import { fetchPlgAuthConfig, requestMagicLink, startPlgAuth } from "plg/api/plgAuth";
import { isSignupBlocked } from "plg/utils/signupBlocked";
import { clearHumanPass, getHumanPass } from "plg/auth/humanPass";
import { applyAuthStartResult, errorFromLoadFailure, REASON_ERROR } from "plg/screens/Signup/authStartResult";
import type { SignInError } from "plg/screens/Signup/authStartResult";
import { clearSignupSession, PLG_EXPIRED_QUERY } from "plg/utils/signupSession";
import { consumeCapturedLinkToken, stripTokenFromAddressBar } from "plg/utils/sensitiveUrl";
import { PLG_SITE_URL } from "plg/routes";
import type { PlgAuthConfig, PlgAuthStartBody } from "plg/types";
import { PLG_SIGNUP_ROUTE } from "constants/routes";
import usePlgMsal from "plg/auth/usePlgMsal";
import type { SsoResult } from "plg/auth/ssoRedirect";

type StartBody = PlgAuthStartBody extends infer B ? (B extends unknown ? Omit<B, "humanPass"> : never) : never;

/**
 * Read ?link=… once and drop it from the address bar. index.tsx normally captured it already, before
 * Sentry and analytics started (see captureLinkTokenEarly); the URL is the fallback.
 */
function takeLinkToken(searchParams: URLSearchParams): string | null {
  const early = consumeCapturedLinkToken();
  if (early !== null) return early;
  const token = searchParams.get("link");
  if (token !== null) stripTokenFromAddressBar();
  return token;
}

function PlgSignInPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [linkToken, setLinkToken] = useState<string | null>(() => takeLinkToken(searchParams));
  // Sign-up (default) or sign-in: the same options either way, only the wording and the footer switch.
  // Page state only, no separate URL.
  const [view, setView] = useState<"signup" | "signin">("signup");
  const { config } = usePlgSignupConfig();
  const [authConfig, setAuthConfig] = useState<PlgAuthConfig | null>(null);
  const [error, setError] = useState<SignInError | null>(() => REASON_ERROR[searchParams.get("reason") || ""] || null);
  const [passed, setPassed] = useState(() => !!getHumanPass());
  const [submitting, setSubmitting] = useState(false);
  const [focusEmail, setFocusEmail] = useState(false);
  // Prototype renderMagic: once a link is sent, "Check your inbox" replaces the whole card.
  const [sentTo, setSentTo] = useState<string | null>(null);
  // What was typed last, so "Use a different address" can fix a typo instead of starting over (QA ON-15).
  const [lastEmail, setLastEmail] = useState("");
  // Reached from the business-email screen, or blocked before in this browser: the footer offers Back to
  // it (prototype blocked variant, QA ON-12).
  const [fromBlocked, setFromBlocked] = useState(() => searchParams.get("from") === "blocked" || isSignupBlocked());
  const expired = searchParams.get(PLG_EXPIRED_QUERY) === "1";

  useEffect(() => {
    fetchPlgAuthConfig()
      .then(setAuthConfig)
      .catch((err) => setError(errorFromLoadFailure(err)));
  }, []);

  const requireHuman = () => {
    clearHumanPass();
    setPassed(false);
  };

  const begin = async (body: StartBody) => {
    if (submitting) return;
    const humanPass = getHumanPass();
    if (!humanPass) {
      requireHuman();
      return;
    }
    setSubmitting(true);
    setError(null);
    clearSignupSession();
    const res = await startPlgAuth({ ...body, humanPass } as PlgAuthStartBody);
    setSubmitting(false);
    const next = applyAuthStartResult(res, navigate, config);
    if (next === "human") requireHuman();
    else setError(next);
  };

  // Back from Google (router state from the callback page) or Microsoft (usePlgMsal): finish the sign-in once.
  const location = useLocation();
  const { msal, result: microsoftResult } = usePlgMsal(authConfig);
  const ssoResult = ((location.state as { sso?: SsoResult } | null)?.sso ?? microsoftResult) || null;
  const ssoHandled = useRef(false);
  useEffect(() => {
    if (!ssoResult || ssoHandled.current) return;
    ssoHandled.current = true;
    // Drop the code from history, so Back or a reload never sends it again.
    if (ssoResult.provider === "google") navigate(location.pathname, { replace: true, state: null });
    begin(
      ssoResult.provider === "google"
        ? { provider: "google", code: ssoResult.code, codeVerifier: ssoResult.codeVerifier }
        : { provider: "microsoft", idToken: ssoResult.idToken, nonce: ssoResult.nonce }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ssoResult]);

  const signUpAgain = () => {
    setError(null);
    setSentTo(null);
    setFromBlocked(true);
    navigate(`${PLG_SIGNUP_ROUTE}?from=blocked`, { replace: true });
  };

  if (error === "country") {
    return (
      <PlgSignupLayout>
        <CountryBlockedCard />
      </PlgSignupLayout>
    );
  }

  if (error === "blocked") {
    return (
      <PlgSignupLayout>
        <BusinessEmailCard onSignUpAgain={signUpAgain} />
      </PlgSignupLayout>
    );
  }

  if (sentTo !== null) {
    return (
      <PlgSignupLayout>
        <MagicLinkSentCard
          email={sentTo}
          onBack={() => {
            setLastEmail(sentTo);
            setSentTo(null);
            setFocusEmail(true);
          }}
          onResend={async () => {
            const humanPass = getHumanPass();
            if (!humanPass) {
              // The human pass ran out: back to the card, where the gate asks again.
              setLastEmail(sentTo);
              setSentTo(null);
              requireHuman();
              return false;
            }
            const res = await requestMagicLink({ email: sentTo, humanPass });
            return res.ok === true;
          }}
        />
      </PlgSignupLayout>
    );
  }

  if (linkToken !== null) {
    return (
      <PlgSignupLayout width="md">
        <MagicLinkLanding
          token={linkToken}
          config={config}
          onRestart={(opts) => {
            if (opts?.fromBlocked) setFromBlocked(true);
            setLinkToken(null);
          }}
        />
      </PlgSignupLayout>
    );
  }

  return (
    <PlgSignupLayout>
      {view === "signin" ? (
        <>
          <h1 className="plg-signup__title">Sign in to your account</h1>
          <p className="plg-signup__sub">Continue discovering talent</p>
        </>
      ) : (
        <>
          <h1 className="plg-signup__title">Create your free account</h1>
          <p className="plg-signup__sub">Browse vetted professionals. No contract or payment until you’re ready to hire.</p>
        </>
      )}

      {expired && !error && (
        <div className="plg-signup__note" role="status">
          Your session expired, please sign in again
        </div>
      )}

      {!passed ? (
        <HumanGate siteKey={authConfig?.turnstile?.siteKey} onPassed={() => setPassed(true)} onBlocked={setError} />
      ) : (
        <>
          <div className="plg-signup__sso">
            <GoogleSsoButton config={authConfig} disabled={submitting} />
            <MicrosoftSsoButton msal={msal} disabled={submitting} />
          </div>

          <SignInErrorNote error={error} />

          <MagicLinkForm
            disabled={submitting}
            autoFocus={focusEmail}
            initialEmail={lastEmail}
            onBlocked={setError}
            onHumanRequired={requireHuman}
            onSent={setSentTo}
          />
        </>
      )}

      {!passed && <SignInErrorNote error={error} />}

      {fromBlocked ? (
        <div className="plg-signup__foot">
          <button type="button" className="plg-signup__foot-link" onClick={() => setError("blocked")}>
            Back
          </button>
          <span>Business accounts get access straight away.</span>
        </div>
      ) : (
        <div className="plg-signup__foot">
          <a href={PLG_SITE_URL}>Back to site</a>
          {view === "signin" ? (
            <span>
              Create new account?{" "}
              <button type="button" className="plg-signup__foot-link" onClick={() => setView("signup")}>
                Sign up
              </button>
            </span>
          ) : (
            <span>
              Already have an account?{" "}
              <button type="button" className="plg-signup__foot-link" onClick={() => setView("signin")}>
                Log in
              </button>
            </span>
          )}
        </div>
      )}
    </PlgSignupLayout>
  );
}

export default PlgSignInPage;
